/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment'
import { v4 as UUID } from 'uuid'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { ProjectDiscountPayload } from 'src/core/models'
import { Button, Divider, Form, Row, Select } from 'antd'
import styles from './ProjectFormDiscountModal.module.scss'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { getFormatedPrice, getProjectUnit, getSymbolFromCurrency } from 'src/core/helpers'
import {
  CustomCol,
  CustomText,
  CustomModal,
  CustomTitle,
  CustomSelect,
  CustomInputNumber,
  CustomInputDecimal,
  CustomDateRangeInput,
} from 'src/components'
import {
  projectCurrencyTokenCodeSelected,
  lessVintagePriceSelector,
  projectFormStateSelector,
  setProjectFormItemValue,
  projectDetailsSelector
} from 'src/features'

interface ProjectFormDiscountModalProps {
  isModalVisible: boolean
  handleCancel: () => void
  reopenModal: () => void
}

const ProjectFormDiscountModal: React.FC<ProjectFormDiscountModalProps> = (props) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { isModalVisible, handleCancel, reopenModal } = props
  const [selectedDiscountType, setSelectedDiscountType] = useState<'FLASH_SALE' | 'VOLUME_DISCOUNT'>(null)
  const [selectedAmountType, setSelectedAmountType] = useState<'PERCENTAGE' | 'FLAT_AMOUNT'>(null)
  const { is_vintage, vintage_items, kind, discounts, currentDiscount } = useAppSelector(projectFormStateSelector)
  const currencyCode = useAppSelector(projectCurrencyTokenCodeSelected)
  const lessPrice = useAppSelector(lessVintagePriceSelector)
  const { project: projectToEdit } = useAppSelector(projectDetailsSelector)

  const handleFinish = (values: ProjectDiscountPayload) => {
    let newList: ProjectDiscountPayload[] = []
    const newDiscount: ProjectDiscountPayload = {
      ...values,
      id: currentDiscount ? currentDiscount.id : UUID(),
      date_start: values.date_start ? moment(values.date_start).format('YYYY-MM-DD') : null,
      date_end: values.date_end ? moment(values.date_end).format('YYYY-MM-DD') : null,
      vintages_data: values.vintages ? values.vintages?.map(v => {
        return { id: v, year: v }
      }) : [],
    }
    if (currentDiscount) {
      newList = discounts.map(discount => {
        if (discount.id !== currentDiscount.id) { return discount }
        return newDiscount
      })
      dispatch(setProjectFormItemValue({ key: 'currentDiscount', value: null }))
    } else {
      newList =  discounts ? [...discounts, newDiscount] : [newDiscount]
    }
    dispatch(setProjectFormItemValue({ key: 'discounts', value: newList }))
    onCancel()
  }

  const onCancel = () => {
    resetFields()
    handleCancel()
  }

  const resetFields = () => {
    form.resetFields()
    setSelectedDiscountType(null)
    setSelectedAmountType(null)
    dispatch(setProjectFormItemValue({ key: 'currentDiscount', value: null }))
  }

  useEffect(() => {
    if (currentDiscount) {
      reopenModal()
      setSelectedDiscountType(currentDiscount.type)
      setSelectedAmountType(currentDiscount.amount_type)
      form.setFieldsValue({
        ['type']: currentDiscount.type,
        ['date_start']: currentDiscount.date_start && moment(new Date(currentDiscount.date_start)),
        ['date_end']: currentDiscount.date_end && moment(new Date(currentDiscount.date_end)),
        ['min_volume']: currentDiscount.min_volume,
        ['amount_type']: currentDiscount.amount_type,
        ['amount']: currentDiscount.amount,
        ['project']: currentDiscount.project,
        ['vintages']: currentDiscount.vintages_data?.map(v => v.year),
      })
    }
  }, [currentDiscount])

  return <CustomModal
    title={currentDiscount ? t('Discount.Edit') : t('Discount.Create')}
    footer={null}
    open={isModalVisible}
    onCancel={onCancel}
  >
    <Form onFinish={handleFinish} form={form}>

      {/* Discount Type */}
      <CustomText label={t('Discount.Type')} />
      <CustomSelect
        onChange={(value) => setSelectedDiscountType(value as 'FLASH_SALE' | 'VOLUME_DISCOUNT')}
        disabled={currentDiscount && projectToEdit?.discounts?.some(d => d?.id === currentDiscount?.id)}
        placeholder={t('Discount.TypePlaceholder')}
        showSearch={false}
        name='type'
        required
      >
        <Select.Option value='FLASH_SALE'>{t('Discount.FlashSale')}</Select.Option>
        <Select.Option value='VOLUME_DISCOUNT'>{t('Discount.VolumeDiscount')}</Select.Option>
      </CustomSelect>

      {/* Discount Dates */}
      {selectedDiscountType === 'FLASH_SALE' && <CustomDateRangeInput />}

      {/* Min Volume */}
      {selectedDiscountType === 'VOLUME_DISCOUNT' && <>
        <CustomText label={t('Discount.MinVolume')} />
        <CustomInputNumber
          placeholder={t('Discount.MinVolumePlaceholder')}
          name='min_volume'
          required
        />
      </>}

      {/* Vintages */}
      {is_vintage && <>
        <CustomText label={t('Projects.Vintages')} />
        <CustomSelect
          placeholder={t('Discount.VintagePlaceholder')}
          name='vintages'
          mode='multiple'
          showSearch={false}
          required
        >
          {vintage_items.map((vintage, idx) => (
            <Select.Option value={vintage.year} key={idx}>
              {vintage.year} - {getFormatedPrice(vintage.price, currencyCode)} - {vintage.stock} {getProjectUnit(kind)}
            </Select.Option>
          ))}
        </CustomSelect>
      </>}

      {/* Amount Type */}
      <CustomText label={t('Discount.AmountType')} />
      <CustomSelect
        onChange={(value) => setSelectedAmountType(value as 'PERCENTAGE' | 'FLAT_AMOUNT')}
        placeholder={t('Discount.AmountTypePlaceholder')}
        showSearch={false}
        name='amount_type'
        required
      >
        <Select.Option value='PERCENTAGE'>{t('Discount.Percentage')}</Select.Option>
        <Select.Option value='FLAT_AMOUNT'>{t('Discount.FlatAmount')}</Select.Option>
      </CustomSelect>

      {/* Amount */}
      <Divider />
      <Row className={styles.amountContent}>
        <CustomCol xs={12}>
          <CustomTitle label={t('Deposits.Amount')} className={styles.amount} />
        </CustomCol>
        <CustomCol xs={12}>
          <CustomInputDecimal
            min={0}
            required
            name='amount'
            disabled={!selectedAmountType}
            placeholder={t('Discount.AmountPlaceholder')}
            max={selectedAmountType === 'PERCENTAGE' ? 99 : lessPrice - 0.1}
            prefix={selectedAmountType === 'PERCENTAGE' ?
              <CustomTitle label={'%'} className={styles.amountLabel} /> :
              <CustomTitle label={getSymbolFromCurrency(currencyCode)} className={styles.amountLabel}/>
            }
          />
        </CustomCol>
      </Row>

      {/* Submit Button */}
      <Divider />
      <Button
        block
        size='large'
        type='primary'
        htmlType='submit'
        className='radius-100'
      >{currentDiscount ? t('Projects.Confirmchanges') : t('general.Create')}
      </Button>
    </Form>
  </CustomModal>
}

export default ProjectFormDiscountModal
